import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, createElementBlock as _createElementBlock, withModifiers as _withModifiers, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6c229f03"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "page" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", null, "New Lead", -1))
const _hoisted_3 = ["onSubmit"]
const _hoisted_4 = /*#__PURE__*/_createTextVNode(" First Name ")
const _hoisted_5 = /*#__PURE__*/_createTextVNode(" Last Name ")
const _hoisted_6 = /*#__PURE__*/_createTextVNode(" Email ")
const _hoisted_7 = /*#__PURE__*/_createTextVNode(" Phone ")
const _hoisted_8 = /*#__PURE__*/_createTextVNode(" Desired Move In Date ")
const _hoisted_9 = { key: 0 }
const _hoisted_10 = /*#__PURE__*/_createTextVNode(" Notes ")
const _hoisted_11 = { class: "actions" }
const _hoisted_12 = ["disabled"]

import { ref } from "vue";
import { useRouter } from "vue-router";
import BaseAlert from "@/components/BaseAlert.vue";
import BaseSpinner from "@/components/BaseSpinner.vue";
import api from "@virgodev/bazaar/functions/api";


export default /*#__PURE__*/_defineComponent({
  props: {
  facilityId: {
    type: String,
    default: null,
  },
  facilityName: {
    type: String,
    default: null,
  },
},
  setup(__props) {

const props = __props

const router = useRouter();


const form = ref({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  phone_label: "Phone",
  desired_move_in_date: "",
  notes: "",
});

const submitting = ref(false);
const errors = ref("");
const dupError = ref("");
const storedge = ref(false);

function checkStoredge() {
  const name = props.facilityName ? props.facilityName.toLowerCase() : "";
  storedge.value = name === "storedge";
}
checkStoredge();

function normalizedPhone(): string {
  // Strip non-digits so we can validate and send a clean number
  return form.value.phone.replace(/\D/g, "");
}

function normalizeEmail(): string {
  return form.value.email.trim().toLowerCase();
}

function normalizePhoneEntry(entry: unknown): string {
  if (!entry || typeof entry !== "string") return "";
  return entry.replace(/\D/g, "");
}

async function fetchCustomers(query: string) {
  // Reuse customers endpoint for duplicate checks
  const resp = await api({
    url: `customers/?facilityId=${props.facilityId || ""}${query}`,
  });
  if (resp.status === 200 && "body" in resp) {
    // tenant inc customers endpoint returns an array
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return Array.isArray(resp.body) ? resp.body : resp.body.customers || [];
  }
  return [];
}

async function findDuplicateCustomer() {
  // Basic dedupe on email and phone before creating a lead
  const email = normalizeEmail();
  const phoneDigits = normalizedPhone();
  const candidates: Array<Record<string, unknown>> = [];

  if (phoneDigits) {
    const byPhone = await fetchCustomers(`&customer_phone=${phoneDigits}`);
    candidates.push(...byPhone);
  }

  const params = [];
  if (form.value.first_name) {
    params.push(`first_name=${encodeURIComponent(form.value.first_name)}`);
  }
  if (form.value.last_name) {
    params.push(`last_name=${encodeURIComponent(form.value.last_name)}`);
  }
  if (params.length) {
    const byName = await fetchCustomers(`&${params.join("&")}`);
    candidates.push(...byName);
  }

  for (const customer of candidates) {
    const customerEmail = (customer as { email?: string }).email;
    if (
      email &&
      customerEmail &&
      typeof customerEmail === "string" &&
      customerEmail.trim().toLowerCase() === email
    ) {
      return true;
    }
    const phones =
      (customer as { phone_numbers?: Array<Record<string, string>> })
        .phone_numbers || [];
    for (const phoneEntry of phones) {
      const digits = normalizePhoneEntry(
        phoneEntry.unformatted_number ||
          phoneEntry.formatted_number ||
          phoneEntry.number
      );
      if (digits && digits === phoneDigits) return true;
    }
  }
  return false;
}

async function submit() {
  errors.value = "";
  dupError.value = "";
  if (
    !form.value.first_name.trim() ||
    !form.value.last_name.trim() ||
    !form.value.phone.trim()
  ) {
    errors.value = "First name, last name, and phone are required.";
    return;
  }

  submitting.value = true;

  const duplicate = await findDuplicateCustomer();
  if (duplicate) {
    dupError.value =
      "A customer with this phone or email already exists. Please use the existing record.";
    submitting.value = false;
    return;
  }

  const phoneDigits = normalizedPhone();
  if (!phoneDigits) {
    errors.value = "Phone number is required.";
    submitting.value = false;
    return;
  }

  // Normalize phone before sending to API; keep other fields trimmed
  const payload = {
    first_name: form.value.first_name.trim(),
    last_name: form.value.last_name.trim(),
    email: form.value.email.trim() || null,
    phone_numbers_attributes: [
      { label: form.value.phone_label, number: phoneDigits },
    ],
    desired_move_in_date: form.value.desired_move_in_date || null,
    notes: form.value.notes.trim(),
  };

  const response = await api({
    url: `leads/?facilityId=${props.facilityId || ""}`,
    method: "POST",
    json: payload,
  });

  submitting.value = false;

  if ([200, 201].includes(response.status)) {
    router.push({
      name: "leads",
      params: {
        facilityId: props.facilityId,
        facilityName: props.facilityName,
      },
    });
  } else {
    errors.value =
      "Unable to create lead. Please check the details and try again.";
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    (dupError.value)
      ? (_openBlock(), _createBlock(BaseAlert, {
          key: 0,
          variant: "danger",
          class: "mb-2"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(dupError.value), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (errors.value)
      ? (_openBlock(), _createBlock(BaseAlert, {
          key: 1,
          variant: "danger",
          class: "mb-2"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(errors.value), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createElementVNode("form", {
      class: "form",
      onSubmit: _withModifiers(submit, ["prevent"])
    }, [
      _createElementVNode("label", null, [
        _hoisted_4,
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.first_name) = $event)),
          type: "text",
          required: ""
        }, null, 512), [
          [_vModelText, form.value.first_name]
        ])
      ]),
      _createElementVNode("label", null, [
        _hoisted_5,
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.last_name) = $event)),
          type: "text",
          required: ""
        }, null, 512), [
          [_vModelText, form.value.last_name]
        ])
      ]),
      _createElementVNode("label", null, [
        _hoisted_6,
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.email) = $event)),
          type: "email"
        }, null, 512), [
          [_vModelText, form.value.email]
        ])
      ]),
      _createElementVNode("label", null, [
        _hoisted_7,
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.phone) = $event)),
          type: "text",
          required: ""
        }, null, 512), [
          [_vModelText, form.value.phone]
        ])
      ]),
      _createElementVNode("label", null, [
        _hoisted_8,
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.value.desired_move_in_date) = $event)),
          type: "date"
        }, null, 512), [
          [_vModelText, form.value.desired_move_in_date]
        ])
      ]),
      (!storedge.value)
        ? (_openBlock(), _createElementBlock("label", _hoisted_9, [
            _hoisted_10,
            _withDirectives(_createElementVNode("textarea", {
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.notes) = $event)),
              rows: "3"
            }, null, 512), [
              [_vModelText, form.value.notes]
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("button", {
          class: "btn btn-blue",
          type: "submit",
          disabled: submitting.value
        }, " Create Lead ", 8, _hoisted_12),
        (submitting.value)
          ? (_openBlock(), _createBlock(BaseSpinner, { key: 0 }))
          : _createCommentVNode("", true)
      ])
    ], 40, _hoisted_3)
  ]))
}
}

})